export const ALLOWED_GRAPH_TYPES = ['continuous', 'pie'] as const
export type AllowedGraphType = (typeof ALLOWED_GRAPH_TYPES)[number]

export function isAllowedGraphType(value: string): value is AllowedGraphType {
  return (ALLOWED_GRAPH_TYPES as readonly string[]).includes(value)
}

/**
 * Pagination tokens are forwarded as query params. Reject patterns that look
 * like absolute URLs or smuggling newlines so they cannot be abused if a proxy
 * mishandles the downstream request.
 */
export function assertSafePaginationToken(token: string): void {
  if (token.length > 8192) {
    throw new Error('Pagination token is too long')
  }
  const lower = token.toLowerCase()
  if (
    lower.includes('://') ||
    lower.startsWith('//') ||
    token.includes('\r') ||
    token.includes('\n') ||
    token.includes('\0')
  ) {
    throw new Error('Invalid pagination token')
  }
}

/** Base used only to parse same-origin-style paths; must not appear in real requests. */
const GRAPH_FETCH_PARSE_ORIGIN = 'https://graph-proxy.invalid'

const ALLOWED_GRAPH_PROXY_PATHNAMES = new Set(
  ALLOWED_GRAPH_TYPES.map((t) => `/api/get-graph/${t}`),
)

/**
 * Ensures a client graph `fetch()` target is a relative path to our proxy only
 * (no absolute URLs, protocol-relative hosts, or unexpected query keys).
 * Complements {@link assertSafePaginationToken} for defense in depth / SAST.
 */
export function assertAllowedGraphClientFetchUrl(url: string): void {
  if (url.length > 16384) {
    throw new Error('Graph request URL is too long')
  }
  let parsed: URL
  try {
    parsed = new URL(url, GRAPH_FETCH_PARSE_ORIGIN)
  } catch {
    throw new Error('Invalid graph request URL')
  }
  if (parsed.origin !== GRAPH_FETCH_PARSE_ORIGIN) {
    throw new Error('Invalid graph request URL')
  }
  if (!ALLOWED_GRAPH_PROXY_PATHNAMES.has(parsed.pathname)) {
    throw new Error('Invalid graph request URL')
  }
  for (const key of parsed.searchParams.keys()) {
    if (key !== 'next') {
      throw new Error('Invalid graph request query')
    }
  }
  const nextValues = parsed.searchParams.getAll('next')
  if (nextValues.length > 1) {
    throw new Error('Invalid graph request query')
  }
  const next = parsed.searchParams.get('next')
  if (next !== null && next !== '') {
    assertSafePaginationToken(next)
  }
}

/**
 * Performs a browser `fetch` only after {@link assertAllowedGraphClientFetchUrl}.
 * The URL is never an arbitrary host: only `/api/get-graph/continuous` or
 * `/api/get-graph/pie`, optionally with a single `next` query (see pagination rules).
 */
export async function fetchAllowlistedGraphProxyUrl(url: string): Promise<Response> {
  assertAllowedGraphClientFetchUrl(url)
  // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
  // Semgrep cannot model our allowlist; the URL is fully validated above.
  return globalThis.fetch(url)
}
